import { decodeDeviceText, watchDeviceText } from './auzi-device-text.js?v=family-20261006-1';
import { rankDevices, normalize } from './auzi-device-search.js?v=family-20261006-1';
import { getDevice, getSelection, confirmDevice, clearDevice, refreshDevice, beginDeviceEdit, deferDevice } from './auzi-device-state.js?v=dropdown-state-20261006-2';
import './auzi-device-product.js?v=dropdown-product-20261006-2';
import './auzi-device-collection.js?v=20261013-device-image-fix-5';
import './auzi-product-steps.js?v=hearloom-flow-2';
import { savePhoto } from './auzi-device-photo.js?v=stage1-6';

const root = document.querySelector('auzi-device-picker');
if (root) initialize(root);

// The product retains its original dropdowns. Only their saved selection is shared.
document.addEventListener('auzi:native-device-selected', event => {
  const { device, context, gift } = event.detail || {};
  if (device) confirmDevice(device, context || 'native:product', true, Boolean(gift));
});
document.addEventListener('auzi:native-device-edit', event => {
  const { family, gift, context } = event.detail || {};
  beginDeviceEdit(family || 'hearing', Boolean(gift), context || 'native:product');
});
document.addEventListener('auzi:native-device-later', event => {
  deferDevice(event.detail?.family || 'hearing', Boolean(event.detail?.gift));
});
document.addEventListener('auzi:gift-mode-changed', event => {
  if (!document.querySelector('[data-auzi-cover-device]')) return;
  const selected = getSelection();
  const gift = Boolean(event.detail?.active);
  if (selected.device && selected.gift !== gift) confirmDevice(selected.device, 'native:product', true, gift);
});

function initialize(root) {
  const config = JSON.parse(root.querySelector('[data-config]').textContent);
  config.assetBase = new URL(config.assetBase, window.location.href).href;

  Object.keys(config.messages || {}).forEach(key => {
    if (typeof config.messages[key] === 'string') config.messages[key] = decodeDeviceText(config.messages[key]);
  });
  const text = config.messages;
  const decodePickerText = decodeDeviceText;
  watchDeviceText(root);
  const dialog = root.querySelector('dialog');
  const search = root.querySelector('[data-search]');
  const results = root.querySelector('[data-results]');
  const status = root.querySelector('[data-status]');
  const form = root.querySelector('[data-manual-form]');
  const brands = root.querySelector('[data-brands]');
  const heading = root.querySelector('[data-list-heading]');
  const searchExamples = {
    hearing: text.searchExampleHearing,
    cochlear: text.searchExampleCochlear
  };
  let catalogue, loading, brand = '', family = 'hearing', context = '', opener, current = getDevice(), lockedFamily = false, gift = false, photoUrl = '';

  const isQuizContext = () => context.startsWith('quiz-');
  const isProductContext = () => Boolean(context) && !isQuizContext();

  function element(tag, className, value) {
    const el = document.createElement(tag);
    el.className = className;
    if (value) el.textContent = value;
    return el;
  }

  function picture(url, alt, className) {
    const image = element('img', className);
    image.src = new URL(url, config.assetBase).href;
    image.alt = alt;
    image.width = className === 'ad-logo' ? 160 : 320;
    image.height = className === 'ad-logo' ? 64 : 260;
    image.loading = 'lazy';
    image.addEventListener('error', () => { image.hidden = true; });
    return image;
  }

  function updateSummary() {
    const productFamily = document.querySelector('auzi-product-device')?.dataset.family;
    const shown = current && (!productFamily || current.family === productFamily) ? current : null;
    document.querySelectorAll('[data-ad-global-label]').forEach(el => {
      el.textContent = shown ? ([shown.brand, shown.model].filter(Boolean).join(' ') || text.photoAdded) : text.choose;
      const button = el.closest('.ad-header-button');
      if (button) { button.classList.toggle('ad-header-saved', Boolean(shown)); button.querySelector('.ad-header-icon').textContent = shown ? '✓' : '＋'; }
    });
    updateSavedDevice();
  }

  function updateSavedDevice() {
    root.querySelector('[data-saved-device]').hidden = !current || !root.querySelector('[data-confirmed]').hidden;
    root.querySelector('[data-saved-name]').textContent = current
      ? [current.brand, current.model].filter(Boolean).join(' ') || text.confirmed : '';
  }

  function updateFamily() {
    search.placeholder = searchExamples[family] || searchExamples.hearing;
    root.querySelectorAll('[data-family]').forEach(button => button.setAttribute('aria-pressed', button.dataset.family === family));
  }

  function renderBrands() {
    brands.replaceChildren();
    let visibleBrands = catalogue.brands.filter(item => !family || item.family === family);

    // Keep the first hearing-aid screen curated. All other manufacturers remain searchable.
    if (family === 'hearing') {
      const featuredBrands = ["Amplifon","Bernafon","Danalogic","Jabra","Oticon","Phonak","ReSound","Rexton","Signia","Starkey","Unitron","Widex"];
      visibleBrands = visibleBrands.filter(item => featuredBrands.includes(item.name));
    }
    visibleBrands.sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
    );

    visibleBrands.forEach(item => {
      const button = element('button', 'ad-brand' + (item.darkLogo ? ' ad-brand-dark' : '') + ' ad-brand-' + item.name.toLowerCase().replace(/[^a-z]/g, ''));
      button.type = 'button';
      button.setAttribute('aria-label', item.name);
      button.title = item.name;
      // Logo only where we have the real brand asset. New/global brands fall
      // back to a simple wordmark so the chooser never shows an empty tile.
      if (item.logo) button.append(picture(item.logo, item.name, 'ad-logo'));
      else button.append(element('strong', 'ad-brand-wordmark', item.name));
      button.addEventListener('click', () => {
        brand = item.name;
        search.value = '';

        // All surfaces use the same family-level catalogue.
        const hasCatalogueModels = catalogue.devices.some(device => device.brand === item.name && device.family === family);
        if (!hasCatalogueModels) {
          showManual(true);
          return;
        }

        // Product, quiz and gifting all use the same family-level catalogue.
        renderResults();
        if (!isQuizContext()) search.focus();
      });
      brands.append(button);
    });
  }

  function select(device) {
    const unknown = device?.model === 'Device unknown';
    device.image = device.image ? new URL(device.image, config.assetBase).href : '';
    device.logo = catalogue?.brands.find(item => item.name === device.brand)?.logo || '';
    if (device.logo) device.logo = new URL(device.logo, config.assetBase).href;

    const confirmedHeading = root.querySelector('[data-confirmed-heading]');
    const confirmedName = root.querySelector('[data-confirmed-name]');
    const confirmedBody = root.querySelector('[data-confirmed-body]');

    const successPanel = root.querySelector('[data-confirmed]');
    if (successPanel) successPanel.classList.toggle('is-no-device', Boolean(unknown && isQuizContext()));

    if (unknown && isQuizContext()) {
      try {
        localStorage.removeItem('auzi.quiz.gift.device.v1');
      } catch {}
      clearDevice();
      current = null;

      const quizId = context.slice(5);
      const quizRoot = document.querySelector('[data-auzi-quiz="' + CSS.escape(quizId) + '"]');
      quizRoot?.dispatchEvent(new CustomEvent('auzi:quiz-device-unknown', {
        detail: { family }
      }));

      if (confirmedHeading) confirmedHeading.textContent = decodePickerText(text.noDeviceTitle);
      if (confirmedName) {
        confirmedName.hidden = false;
        confirmedName.textContent = decodePickerText(text.noDeviceLabel);
      }
      if (confirmedBody) confirmedBody.textContent = decodePickerText(text.noDeviceHeading);
    } else {
      if (isQuizContext() && gift) {
        try {
          localStorage.removeItem('auzi.quiz.gift.device.v1');
        } catch {}
        clearDevice();
      } else if (isQuizContext()) {
        try {
          localStorage.removeItem('auzi.quiz.gift.device.v1');
        } catch {}
      }

      current = confirmDevice(
        { ...device, source: device.source || 'catalogue' },
        context,
        true,
        gift
      );

      if (isQuizContext() && gift) {
        const quizId = context.slice(5);
        const quizRoot = document.querySelector('[data-auzi-quiz="' + CSS.escape(quizId) + '"]');
        try {
          localStorage.setItem('auzi.quiz.gift.device.v1', JSON.stringify(current));
        } catch {}
        quizRoot?.dispatchEvent(new CustomEvent('auzi:quiz-device-sync', { detail: { device: current } }));
      }

      if (isQuizContext() && !gift) {
        try {
          localStorage.setItem('auzi.quiz.device.v1', JSON.stringify(current));
          sessionStorage.setItem('auzi.device.session.v2', JSON.stringify(current));
        } catch {}
      }

      if (confirmedHeading) confirmedHeading.textContent = text.confirmed;
      confirmedName.hidden = false;
      confirmedName.textContent =
        [device.brand, device.model].filter(Boolean).join(' ') || text.yourDevicePhoto;
      if (confirmedBody) confirmedBody.textContent = text.confirmedBody;
    }

    root.querySelector('[data-photo-panel]').hidden = true;
    root.querySelector('[data-done]').textContent = context ? text.done : text.exploreJewellery + ' →';
    root.querySelector('[data-confirmed]').hidden = false;
    root.querySelector('[data-browse]').hidden = true;
    root.querySelector('[data-manual]').hidden = true;
    root.querySelector('[data-done]').focus();

    if (!unknown || !isQuizContext()) {
      updateSavedDevice();
      if (!gift) updateSummary();
    }
  }

  function renderResults() {
    const quiz = isQuizContext();
    const product = isProductContext();
    const searchLabel = root.querySelector('.ad-search-label');
    const fallback = root.querySelector('.ad-fallback');
    const fallbackCopy = fallback?.querySelector('div:first-child');
    const manufacturerFallback = root.querySelector('[data-manufacturer-fallback]');
    const manufacturerFallbackDetail = root.querySelector('[data-manufacturer-fallback-detail]');
    const manualButton = root.querySelector('[data-manual-open]');
    const photoButton = root.querySelector('[data-photo-open]');

    // Search is always available. It is the fastest route for someone who
    // knows their device, while manufacturer -> exact model stays the simple fallback.
    searchLabel.hidden = false;
    search.hidden = false;

    root.querySelector('[data-family-copy]').textContent =
      family === 'cochlear' ? text.familyCopyCochlear : text.familyCopyHearing;

    const query = search.value.trim();
    const normalizedQuery = normalize(query);
    const showBrands = !brand && !query;
    brands.hidden = !showBrands;
    root.querySelector('[data-back]').hidden = !brand;
    root.querySelector('[data-results-wrap]').hidden = showBrands;
    heading.textContent = brand || (showBrands ? text.manufacturers : text.results);
    results.replaceChildren();

    if (manufacturerFallback) {
      manufacturerFallback.hidden = !showBrands || !(quiz || product);
      if (!manufacturerFallback.hidden && manufacturerFallbackDetail) {
        manufacturerFallbackDetail.hidden = quiz;
      }
    }

    if (fallback) {
      fallback.hidden = (showBrands && (quiz || product));
      if (quiz) {
        if (fallbackCopy) fallbackCopy.hidden = false;
        manualButton.innerHTML = text.done + ' <span>→</span>';
        photoButton.hidden = true;
      } else {
        if (fallbackCopy) fallbackCopy.hidden = false;
        manualButton.innerHTML = text.enterDeviceName + ' <span>→</span>';
        photoButton.hidden = false;
      }
    }

    if (showBrands) {
      status.textContent = '';
      return;
    }

    const rankedMatches = rankDevices(catalogue.devices, query, brand, family);
    const directMatches = query
      ? catalogue.devices.filter(device => {
          if (family && device.family !== family) return false;
          if (brand && device.brand !== brand) return false;
          const haystack = normalize([device.brand, device.model, ...(device.aliases || [])].join(' '));
          return haystack.includes(normalizedQuery);
        })
      : [];
    let matches = [...directMatches, ...rankedMatches].filter((device, index, list) =>
      list.findIndex(item => item.id === device.id) === index
    );
    // Quiz and gifting should show the full photographed catalogue for the chosen brand.
    // Search stays capped so global results don't become unwieldy.
    if (query) matches = matches.slice(0, 20);
    else if (brand && family === 'hearing') matches = matches.filter(device => device.featured !== false).slice(0, 8);

    const brandMatches = (!brand && query)
      ? catalogue.brands
          .filter(item => item.family === family && normalize([item.name, ...(item.aliases || [])].join(' ')).includes(normalizedQuery))
          .sort((a, b) => {
            const aExact = normalize(a.name) === normalizedQuery ? 0 : 1;
            const bExact = normalize(b.name) === normalizedQuery ? 0 : 1;
            return aExact - bExact || a.name.localeCompare(b.name);
          })
          .slice(0, 5)
      : [];

    const totalMatches = brandMatches.length + matches.length;
    status.textContent = query
      ? (totalMatches ? '' : text.noResults)
      : (quiz ? '' : (matches.length
          ? (matches.length === 1 ? text.resultSingular : text.resultCount.replace('__COUNT__', matches.length))
          : text.noResults));

    brandMatches.forEach(item => {
      const button = element('button', 'ad-device ad-brand-search-result');
      button.type = 'button';
      if (item.logo) button.append(picture(item.logo, item.name, 'ad-photo ad-brand-search-logo'));
      else button.append(element('span', 'ad-brand-search-wordmark', item.name));
      const copy = element('span', 'ad-device-copy');
      copy.append(
        element('small', 'ad-device-brand', text.brandLabel),
        element('strong', 'ad-device-name', item.name)
      );
      button.append(copy, element('span', 'ad-device-arrow', '→'));
      button.addEventListener('click', () => {
        brand = item.name;
        search.value = '';
        const hasCatalogueModels = catalogue.devices.some(device => device.brand === item.name && device.family === family);
        if (!hasCatalogueModels) {
          showManual(true);
          return;
        }
        renderResults();
      });
      results.append(button);
    });

    matches.forEach(device => {
      const button = element('button', 'ad-device');
      button.type = 'button';
      if (device.image) button.append(picture(device.image, '', 'ad-photo'));
      else button.append(element('span', 'ad-device-placeholder', device.brand.slice(0, 1)));
      const copy = element('span', 'ad-device-copy');
      copy.append(
        element('small', 'ad-device-brand', device.brand),
        element('strong', 'ad-device-name', device.model)
      );
      if (current?.id === device.id) {
        copy.append(element('span', 'ad-selected', '✓ ' + text.confirmed));
      }
      button.append(copy, element('span', 'ad-device-arrow', '→'));
      button.addEventListener('click', () => select(device));
      results.append(button);
    });
  }

  async function load() {
    if (catalogue) return;
    if (!loading) loading = fetch(config.catalogueUrl).then(response => {
      if (!response.ok) throw new Error('Device catalogue unavailable');
      return response.json();
    }).then(data => {
      if (!Array.isArray(data.brands) || !Array.isArray(data.devices)) throw new Error('Invalid catalogue');
      catalogue = data;
    }).catch(error => { loading = null; throw error; });
    return loading;
  }

  async function open(trigger) {
    opener = trigger;
    context = trigger.dataset.adContext || '';
    const guided = Boolean(context);
    gift = trigger.dataset.adGift === 'true' || Boolean(trigger.closest('product-info')?.querySelector('[data-ad-gift-toggle]')?.checked);

    let giftDevice = null;
    if (gift) {
      try {
        const journey = JSON.parse(sessionStorage.getItem('auzi.gift.session.v1') || 'null');
        giftDevice = journey?.active && journey?.knowsDevice ? journey.device : null;
        if (!giftDevice && isQuizContext()) {
          giftDevice = JSON.parse(localStorage.getItem('auzi.quiz.gift.device.v1') || 'null');
        }
      } catch {}
    }

    current = gift
      ? giftDevice
      : (trigger.closest('auzi-product-device')?.device || getDevice());
    // In a cart, the saved device belongs to the line item, not the
    // last device selected anywhere else in the browser.
    if (context.startsWith('cart:')) {
      const savedBrand = trigger.dataset.adInitialBrand || '';
      const savedModel = trigger.dataset.adInitialModel || '';
      current = savedBrand && savedModel
        ? { brand: savedBrand, model: savedModel, family: trigger.dataset.adFamily || 'hearing', id: 'cart-current', source: 'manual' }
        : null;
    }

    root.querySelector('.ad-search-label').hidden = false;
    search.hidden = false;
    root.querySelector('.ad-fallback').hidden = isQuizContext();
    const requestedFamily = trigger.dataset.adFamily || current?.family;
    family = ['hearing', 'cochlear'].includes(requestedFamily) ? requestedFamily : 'hearing';
    lockedFamily = Boolean(trigger.dataset.adFamily);
    root.querySelector('.ad-family-tabs').hidden = lockedFamily;
    root.querySelector('[data-photo-panel]').hidden = true;
    updateFamily();
    // A cart edit starts with the item's existing manufacturer, not an
    // unrelated device remembered from another product or a previous visit.
    const initialBrand = context.startsWith('cart:') ? (trigger.dataset.adInitialBrand || '') : '';
    brand = initialBrand;
    search.value = '';
    root.querySelector('[data-confirmed]').hidden = true;
    root.querySelector('[data-browse]').hidden = false;
    root.querySelector('[data-manual]').hidden = true;
    updateSavedDevice();
    if (!dialog.open) dialog.showModal();
    status.textContent = text.loading;
    try {
      await load();
      // Unknown/manual manufacturer names still allow manual model entry.
      const matchedBrand = initialBrand && catalogue.brands.find(item =>
        item.family === family && String(item.name).trim().toLocaleLowerCase() === initialBrand.trim().toLocaleLowerCase()
      );
      if (initialBrand && !matchedBrand) {
        brand = initialBrand;
        showManual(true);
      } else {
        if (matchedBrand) brand = matchedBrand.name;
        renderBrands();
        renderResults();
        search.focus();
      }
    }
    catch { status.textContent = text.loadError; showManual(); }
  }

  function showManual(fromBrand = false) {
    root.querySelector('[data-manual]').hidden = false;
    root.querySelector('[data-browse]').hidden = true;
    root.querySelector('[data-confirmed]').hidden = true;

    form.elements.brand.value = brand;
    form.elements.model.value = fromBrand ? '' : search.value.trim();
    form.elements.family.value = family || 'hearing';
    root.querySelector('[data-family-field]').hidden = lockedFamily;

    const brandField = form.elements.brand.closest('.ad-field');
    if (brandField) brandField.hidden = Boolean(fromBrand && brand);

    if (fromBrand && brand) {
      const example = catalogue?.devices.find(device => device.brand === brand && device.family === family);
      form.elements.model.placeholder = example ? example.model : '';
      form.elements.model.focus();
    } else {
      form.elements.model.placeholder = '';
      form.elements.brand.focus();
    }
  }

  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-ad-open]');
    if (trigger) { event.preventDefault(); open(trigger); }
  });

  document.addEventListener('auzi:open-device-picker', event => {
    const trigger = event.detail?.trigger;
    if (!trigger) return;
    event.preventDefault?.();
    open(trigger);
  });

  document.addEventListener('auzi:device-picker-unknown', event => {
    const trigger = event.detail?.trigger;
    if (!trigger || !trigger.matches?.('[data-ad-open]')) return;

    opener = trigger;
    context = trigger.dataset.adContext || '';
    gift = trigger.dataset.adGift === 'true' ||
      Boolean(trigger.closest('product-info')?.querySelector('[data-ad-gift-toggle]')?.checked);

    family = event.detail?.family || trigger.dataset.adFamily || 'hearing';
    lockedFamily = Boolean(trigger.dataset.adFamily);
    brand = '';
    search.value = '';

    root.querySelector('.ad-family-tabs').hidden = lockedFamily;
    root.querySelector('[data-photo-panel]').hidden = true;
    root.querySelector('[data-manual]').hidden = true;
    root.querySelector('[data-browse]').hidden = true;
    root.querySelector('[data-confirmed]').hidden = true;

    updateFamily();
    if (!dialog.open) dialog.showModal();

    select({
      id: 'quiz-device-unknown',
      brand: '',
      model: 'Device unknown',
      family,
      source: 'unknown'
    });

    dialog.scrollTop = 0;
  });
  root.querySelector('[data-close]').addEventListener('click', () => dialog.close());
  root.querySelector('[data-done]').addEventListener('click', () => {
    dialog.close();
    if (isQuizContext()) {
      const quizId = context.slice(5);
      const quizRoot = document.querySelector('[data-auzi-quiz="' + CSS.escape(quizId) + '"]');
      quizRoot?.dispatchEvent(new CustomEvent('auzi:quiz-device-continue'));
      return;
    }
    if (!context) window.location.assign(current.family === 'cochlear' ? config.collectionCochlear : config.collectionHearing);
  });
  dialog.addEventListener('close', () => opener?.focus());
  root.querySelector('[data-back]').addEventListener('click', () => { brand = ''; search.value = ''; renderResults(); search.focus(); });
  root.querySelector('[data-manufacturer-fallback]').addEventListener('click', () => {
    brand = '';
    search.value = '';
    showManual(false);
  });

  root.querySelector('[data-manual-open]').addEventListener('click', () => {
    // If they picked a manufacturer but can't see the model, only ask for the model.
    // If no manufacturer was found, ask for manufacturer then model.
    showManual(Boolean(brand));
  });
  root.querySelector('[data-manual-back]').addEventListener('click', () => {
    root.querySelector('[data-manual]').hidden = true;
    root.querySelector('[data-browse]').hidden = false;
    if (isProductContext()) {
      brand = '';
      search.value = '';
    }
    if (catalogue) renderResults();
    if (!isProductContext() && !isQuizContext()) search.focus();
  });
  search.addEventListener('input', () => {
    if (search.value.trim()) brand = '';
    if (catalogue) renderResults();
  });
  form.addEventListener('submit', event => {
    event.preventDefault();
    const brand = form.elements.brand.value.trim();
    const model = form.elements.model.value.trim();
    if (!brand || !model || !form.reportValidity()) return;
    select({ id: 'manual:' + brand + ':' + model, brand, model, family: form.elements.family.value, source: 'manual' });
  });
  root.querySelector('[data-clear]').addEventListener('click', () => {
    const cartContext = context.startsWith('cart:') ? context : '';
    clearDevice(); current = null; updateSummary();
    root.querySelector('[data-confirmed]').hidden = true;
    root.querySelector('[data-manual]').hidden = true;
    root.querySelector('[data-photo-panel]').hidden = true;
    root.querySelector('[data-browse]').hidden = false;
    if (catalogue) renderResults();
    status.textContent = text.cleared;
    if (cartContext) {
      // The global browser selection is not the Shopify line-item selection.
      // Clearing a cart device must persist it to the exact cart line.
      document.dispatchEvent(new CustomEvent('auzi:cart-device-clear', {
        detail: { context: cartContext, key: cartContext.slice(5) }
      }));
      dialog.close();
      return;
    }
    search.focus();
  });
  root.querySelectorAll('[data-family]').forEach(button => button.addEventListener('click', () => {
    family = button.dataset.family; brand = ''; search.value = '';
    updateFamily();
    if (catalogue) { renderBrands(); renderResults(); }
  }));
  const photoForm = root.querySelector('[data-photo-form]');
  const photoStatus = root.querySelector('[data-photo-status]');
  root.querySelector('[data-photo-open]').addEventListener('click', () => {
    root.querySelector('[data-browse]').hidden = true;
    root.querySelector('[data-photo-panel]').hidden = false;
    photoForm.elements.brand.value = brand;
    photoForm.elements.model.value = search.value.trim();
  });
  root.querySelector('[data-photo-back]').addEventListener('click', () => {
    root.querySelector('[data-photo-panel]').hidden = true;
    root.querySelector('[data-browse]').hidden = false;
  });
  photoForm.querySelector('input[type=file]').addEventListener('change', event => {
    const file = event.target.files[0];
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    const preview = root.querySelector('[data-upload-preview]');
    preview.hidden = !file;
    root.querySelector('[data-upload-prompt]').hidden = Boolean(file);
    if (file) { photoUrl = URL.createObjectURL(file); preview.src = photoUrl; }
    photoStatus.textContent = file ? file.name + ' · ' + text.photoChangePrompt : '';
  });
  photoForm.addEventListener('submit', async event => {
    event.preventDefault();
    const button = photoForm.querySelector('[type=submit]');
    button.disabled = true;
    try {
      const saved = await savePhoto(photoForm.querySelector('input[type=file]').files[0]);
      if (!saved.persistent && !context) throw new Error(text.photoSaveError);
      select({ id: 'photo:' + saved.id, photoId: saved.id, photoName: saved.name,
        brand: photoForm.elements.brand.value.trim(), model: photoForm.elements.model.value.trim(), family, source: 'photo' });
    } catch (error) {
      photoStatus.textContent = error.message === 'invalid-image-type' ? text.photoInvalidType
        : error.message === 'image-too-large' ? text.photoTooLarge
        : error.message;
    }
    finally { button.disabled = false; }
  });
  updateSummary();
  document.addEventListener('auzi:device-confirmed', () => { current = getDevice(); updateSummary(); });
  document.addEventListener('auzi:device-cleared', () => { current = null; updateSummary(); });
  // Refresh previously saved catalogue photos; uploaded photos and manual names stay intact.
  if (current?.source === 'catalogue') load().then(() => {
    const saved = getDevice();
    const match = catalogue.devices.find(device => device.id === saved?.id || device.legacyIds?.includes(saved?.id));
    if (!match || saved.source !== 'catalogue') return;
    const logo = catalogue.brands.find(item => item.name === match.brand)?.logo;
    const refreshed = { ...saved, ...match, id: saved.id, image: match.image ? new URL(match.image, config.assetBase).href : '',
      logo: logo ? new URL(logo, config.assetBase).href : '', source: 'catalogue' };
    if (saved.image === refreshed.image && saved.logo === refreshed.logo && saved.model === refreshed.model) return;
    current = refreshDevice(refreshed);
    updateSummary();
  }).catch(() => {});
}

